// ============================================================================
// TEAM CREATE PAGE
// Form for creating a new team
// ============================================================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Users, AlertCircle } from 'lucide-react';
import { useCreateTeam } from '../hooks/useTeams';
import { useAuth } from '../contexts/AuthContext';

export const TeamCreate: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const createTeamMutation = useCreateTeam();

  const [formData, setFormData] = useState({
    teamName: '',
    description: '',
    teamLead: user?.userId || ''
  });
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.teamName.trim()) {
      setError('Team name is required');
      return;
    }

    try {
      const newTeam = await createTeamMutation.mutateAsync({
        teamName: formData.teamName.trim(),
        description: formData.description.trim() || undefined,
        teamLead: formData.teamLead || undefined
      });
      
      // Navigate to the new team's detail page
      navigate(`/teams/${newTeam.teamId}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create team');
    }
  };

  return (
    <div style={{ padding: '32px', maxWidth: '800px', margin: '0 auto' }}>
      {/* Back Button */}
      <button
        onClick={() => navigate('/teams')}
        style={{
          padding: '8px 12px',
          background: 'transparent',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          marginBottom: '24px',
          color: '#475569'
        }}
      >
        <ChevronLeft size={16} />
        Back to Teams
      </button>

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          background: '#00c6c2',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Users size={32} color="white" />
        </div>
        <div>
          <h1 style={{ fontSize: '32px', fontWeight: '700', margin: '0 0 8px 0' }}>
            Create New Team
          </h1>
          <p style={{ color: '#64748b', margin: 0 }}>
            Set up a new team for task assignment and collaboration
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '12px 16px',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          color: '#991b1b'
        }}>
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '24px'
        }}>
          {/* Team Name */}
          <div style={{ marginBottom: '24px' }}>
            <label
              htmlFor="teamName"
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#0f172a',
                marginBottom: '8px'
              }}
            >
              Team Name <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              id="teamName"
              type="text"
              value={formData.teamName}
              onChange={(e) => setFormData({ ...formData, teamName: e.target.value })}
              placeholder="e.g., Engineering Team, Design Team, etc."
              required
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '24px' }}>
            <label
              htmlFor="description"
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#0f172a',
                marginBottom: '8px'
              }}
            >
              Description (Optional)
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the team's purpose and responsibilities..."
              rows={4}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Team Lead (Hidden - auto-set to creator) */}
          <div style={{
            padding: '12px',
            background: '#f8fafc',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#64748b',
            marginBottom: '24px'
          }}>
            <strong>Team Lead:</strong> You will be set as the initial team lead. You can change this later in team settings.
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            paddingTop: '16px',
            borderTop: '1px solid #e2e8f0'
          }}>
            <button
              type="button"
              onClick={() => navigate('/teams')}
              disabled={createTeamMutation.isPending}
              style={{
                padding: '10px 20px',
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                color: '#475569'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createTeamMutation.isPending}
              style={{
                padding: '10px 20px',
                background: createTeamMutation.isPending ? '#94a3b8' : '#00c6c2',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: createTeamMutation.isPending ? 'not-allowed' : 'pointer'
              }}
            >
              {createTeamMutation.isPending ? 'Creating...' : 'Create Team'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};
